import React, { useState, useEffect, useMemo } from "react";
import { createPortal } from "react-dom";
import leaveService from "../../services/leaveService";
import { getErrorMessage } from "../../utils/errorHandler";
import Navbar from "../../components/layout/Navbar";
import Sidebar from "../../components/layout/Sidebar";
import {
  ClipboardList,
  ShieldAlert,
  CheckCircle2,
  ThumbsUp,
  ThumbsDown,
  Search,
  Filter,
  X,
  Eye,
  Paperclip,
  CalendarDays,
  UserRound,
} from "lucide-react";
import "../../styles/dashboard.css";

const EMPTY_FILTERS = { name: "", search: "", status: "ALL" };

const normalizeStatus = (status) => {
  const value = String(status ?? "").trim().toUpperCase();
  if (["APPROVED", "ACCEPTED", "VALIDATED", "APPROUVE", "APPROUVÉ"].includes(value)) return "APPROVED";
  if (["REJECTED", "REFUSED", "REJETE", "REJETÉ", "REFUSÉ"].includes(value)) return "REJECTED";
  if (["CANCELLED", "CANCELED", "ANNULE", "ANNULÉ"].includes(value)) return "CANCELLED";
  if (["PENDING_HR", "PENDING RH"].includes(value)) return "PENDING_HR";
  if (["PENDING_MANAGER", "PENDING MANAGER"].includes(value)) return "PENDING_MANAGER";
  return value || "UNKNOWN";
};

const getEmployeeName = (request) =>
  String(request?.employee_name ?? request?.employeeName ?? "Employé inconnu").trim();

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "-" : date.toLocaleDateString("fr-FR");
};

const getStatusLabel = (status) => {
  switch (normalizeStatus(status)) {
    case "APPROVED":
      return { label: "Approuvé définitivement", className: "badge-success" };
    case "REJECTED":
      return { label: "Rejeté", className: "badge-danger" };
    case "PENDING_HR":
      return { label: "Validé Manager (en attente RH)", className: "badge-warning" };
    case "PENDING_MANAGER":
      return { label: "En attente Manager", className: "badge-info" };
    case "CANCELLED":
      return { label: "Annulé", className: "badge-danger" };
    default:
      return { label: status || "Statut inconnu", className: "badge-info" };
  }
};

const filterRequests = (items, filters) => {
  const nameQuery = filters.name.trim().toLocaleLowerCase("fr-FR");
  const searchQuery = filters.search.trim().toLocaleLowerCase("fr-FR");
  const wantedStatus = filters.status;

  return items.filter((request) => {
    const employeeName = getEmployeeName(request).toLocaleLowerCase("fr-FR");
    const status = normalizeStatus(request?.status);

    if (nameQuery && !employeeName.includes(nameQuery)) return false;
    if (wantedStatus !== "ALL" && status !== wantedStatus) return false;

    if (searchQuery) {
      const searchableText = [
        request?.id,
        employeeName,
        request?.leave_type_name,
        request?.leave_type,
        request?.reason,
        request?.manager_comment,
        request?.start_date,
        request?.end_date,
        request?.duration_days,
        status,
        getStatusLabel(status).label,
      ]
        .filter((value) => value !== null && value !== undefined)
        .join(" ")
        .toLocaleLowerCase("fr-FR");

      if (!searchableText.includes(searchQuery)) return false;
    }

    return true;
  });
};

const filterBarStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
  gap: "12px",
  marginBottom: "18px",
  padding: "14px",
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: "10px",
};

const filterLabelStyle = {
  display: "block",
  marginBottom: "6px",
  color: "#475569",
  fontSize: "0.78rem",
  fontWeight: 700,
};

const controlStyle = {
  width: "100%",
  minHeight: "38px",
  boxSizing: "border-box",
  padding: "8px 10px",
  border: "1px solid #cbd5e1",
  borderRadius: "7px",
  background: "#fff",
  color: "#0f172a",
  fontSize: "0.88rem",
};

const FilterToolbar = ({ title, filters, setFilters, statusOptions, resultCount, totalCount }) => {
  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const clearFilters = () => setFilters({ ...EMPTY_FILTERS });
  const isFiltered = Boolean(filters.name || filters.search || filters.status !== "ALL");

  return (
    <div style={filterBarStyle} aria-label={`Filtres ${title}`}>
      <div>
        <label style={filterLabelStyle}>
          <Search size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />
          Recherche générale
        </label>
        <input
          type="search"
          value={filters.search}
          onChange={(event) => updateFilter("search", event.target.value)}
          placeholder="Référence, type, motif, date..."
          className="form-input"
          style={controlStyle}
          aria-label={`Recherche générale ${title}`}
        />
      </div>

      <div>
        <label style={filterLabelStyle}>
          <Search size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />
          Nom du collaborateur
        </label>
        <input
          type="search"
          value={filters.name}
          onChange={(event) => updateFilter("name", event.target.value)}
          placeholder="Ex. Sana Ben Aicha"
          className="form-input"
          style={controlStyle}
          aria-label={`Nom du collaborateur ${title}`}
        />
      </div>

      <div>
        <label style={filterLabelStyle}>
          <Filter size={13} style={{ verticalAlign: "-2px", marginRight: 5 }} />
          Statut
        </label>
        <select
          value={filters.status}
          onChange={(event) => updateFilter("status", event.target.value)}
          className="form-input"
          style={controlStyle}
          aria-label={`Filtrer par statut ${title}`}
        >
          <option value="ALL">Tous les statuts</option>
          {statusOptions.map((status) => (
            <option key={status.value} value={status.value}>
              {status.label}
            </option>
          ))}
        </select>
      </div>

      <div style={{ display: "flex", flexDirection: "column", justifyContent: "end", gap: 7 }}>
        <span style={{ color: "#64748b", fontSize: "0.8rem" }}>
          {resultCount} résultat(s) sur {totalCount}
        </span>
        <button
          type="button"
          onClick={clearFilters}
          disabled={!isFiltered}
          className="btn"
          style={{
            minHeight: "34px",
            padding: "7px 10px",
            border: "1px solid #cbd5e1",
            borderRadius: "7px",
            background: isFiltered ? "#fff" : "#f1f5f9",
            color: isFiltered ? "#334155" : "#94a3b8",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
            cursor: isFiltered ? "pointer" : "not-allowed",
          }}
        >
          <X size={14} /> Réinitialiser les filtres
        </button>
      </div>
    </div>
  );
};

const TeamLeaveRequests = () => {
  const [requests, setRequests] = useState([]);
  const [historyRequests, setHistoryRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [comments, setComments] = useState({});
  const [pendingFilters, setPendingFilters] = useState({ ...EMPTY_FILTERS });
  const [historyFilters, setHistoryFilters] = useState({ ...EMPTY_FILTERS });
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [attachmentsLoading, setAttachmentsLoading] = useState(false);
  const [attachmentsError, setAttachmentsError] = useState("");

  const openDetails = async (request) => {
    setSelectedRequest(request);
    setAttachments([]);
    setAttachmentsError("");
    setAttachmentsLoading(true);

    try {
      if (typeof leaveService.getAdditionalAttachments !== "function") {
        setAttachmentsError("La consultation des pièces jointes n'est pas disponible dans leaveService.");
        return;
      }

      const result = await leaveService.getAdditionalAttachments(request.id);
      const list = Array.isArray(result)
        ? result
        : Array.isArray(result?.data)
        ? result.data
        : Array.isArray(result?.items)
        ? result.items
        : Array.isArray(result?.attachments)
        ? result.attachments
        : [];
      setAttachments(list);
    } catch (err) {
      console.error("Erreur de chargement des pièces jointes :", err);
      setAttachmentsError("Impossible de charger les pièces jointes de cette demande.");
    } finally {
      setAttachmentsLoading(false);
    }
  };

  const closeDetails = () => {
    setSelectedRequest(null);
    setAttachments([]);
    setAttachmentsError("");
  };

  const fetchTeamRequests = async () => {
    try {
      setError("");
      const response = await leaveService.getLeaveRequestsManager();
      const allRequests = Array.isArray(response) ? response : [];
      setRequests(allRequests.filter((request) => normalizeStatus(request?.status) === "PENDING_MANAGER"));
      setHistoryRequests(allRequests.filter((request) => normalizeStatus(request?.status) !== "PENDING_MANAGER"));
    } catch (err) {
      console.error("Error loading team requests:", err);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTeamRequests();
  }, []);

  const handleDecision = async (requestId, approved) => {
    setError("");
    setSuccess("");
    const comment = comments[requestId] || "";

    try {
      await leaveService.managerDecision(requestId, approved, comment);
      setSuccess(
        `La demande a été ${approved ? "approuvée (transmise aux RH)" : "rejetée"} avec succès.`
      );
      setComments((previous) => {
        const copy = { ...previous };
        delete copy[requestId];
        return copy;
      });
      await fetchTeamRequests();
      window.setTimeout(() => setSuccess(""), 4000);
    } catch (err) {
      console.error("Error submitting decision:", err);
      setError(getErrorMessage(err));
    }
  };

  const handleCommentChange = (requestId, value) => {
    setComments((previous) => ({ ...previous, [requestId]: value }));
  };

  const pendingStatusOptions = useMemo(
    () => [{ value: "PENDING_MANAGER", label: "En attente Manager" }],
    []
  );

  const historyStatusOptions = useMemo(() => {
    const values = new Set(historyRequests.map((request) => normalizeStatus(request?.status)));
    ["PENDING_HR", "APPROVED", "REJECTED", "CANCELLED"].forEach((value) => values.add(value));
    return Array.from(values)
      .filter((value) => value && value !== "UNKNOWN")
      .sort((a, b) => getStatusLabel(a).label.localeCompare(getStatusLabel(b).label, "fr"))
      .map((value) => ({ value, label: getStatusLabel(value).label }));
  }, [historyRequests]);

  const filteredPendingRequests = useMemo(
    () => filterRequests(requests, pendingFilters),
    [requests, pendingFilters]
  );

  const filteredHistoryRequests = useMemo(
    () => filterRequests(historyRequests, historyFilters),
    [historyRequests, historyFilters]
  );

  if (loading) {
    return (
      <div className="flex-center-all" style={{ height: "100vh", background: "#f8fafc", flexDirection: "column" }}>
        <div
          className="spinner"
          style={{ width: "40px", height: "40px", border: "4px solid #e2e8f0", borderTopColor: "#3b82f6", borderRadius: "50%" }}
        />
        <p style={{ marginTop: "15px", color: "#64748b", fontFamily: "sans-serif" }}>
          Récupération des demandes...
        </p>
      </div>
    );
  }

  return (
    <div className="dashboard-layout-container">
      <Navbar />
      <div className="dashboard-content-wrapper">
        <Sidebar />
        <main className="dashboard-main-content">
          <div className="dashboard-welcome-header">
            <div>
              <h2 className="welcome-title">Validations d'Équipe</h2>
              <p className="welcome-subtitle">
                Gérez les demandes de congés déposées par les membres de votre équipe.
              </p>
            </div>
          </div>

          {success && (
            <div className="flash-success-toast">
              <CheckCircle2 size={20} className="toast-icon" />
              <span>{success}</span>
            </div>
          )}
          {error && (
            <div className="form-error-box" style={{ marginBottom: "20px" }}>
              <ShieldAlert size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Pending validations */}
          <section className="dashboard-section card-inner-box">
            <div
              className="card-box-header"
              style={{ marginBottom: "20px", display: "flex", alignItems: "center", gap: "10px" }}
            >
              <ClipboardList size={22} style={{ color: "#eab308" }} />
              <div>
                <h3 style={{ margin: 0 }}>Demandes en attente de votre avis</h3>
                <p style={{ margin: "5px 0 0 0" }}>
                  Vérifiez les dates et les motifs avant de valider le premier niveau.
                </p>
              </div>
            </div>

            <FilterToolbar
              title="des demandes en attente"
              filters={pendingFilters}
              setFilters={setPendingFilters}
              statusOptions={pendingStatusOptions}
              resultCount={filteredPendingRequests.length}
              totalCount={requests.length}
            />

            <div className="table-responsive-wrapper">
              <table className="custom-table">
                <thead>
                  <tr>
                    <th>Collaborateur</th>
                    <th>Type</th>
                    <th>Dates</th>
                    <th className="text-center">Durée</th>
                    <th>Motif</th>
                    <th className="text-center">Détails</th>
                    <th style={{ width: "220px" }}>Commentaire (RH/Salarié)</th>
                    <th style={{ width: "200px" }} className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPendingRequests.map((request) => {
                    const employeeName = getEmployeeName(request);
                    return (
                      <tr
                        key={request.id}
                        onClick={() => openDetails(request)}
                        title="Cliquer pour voir les détails de la demande"
                        style={{ cursor: "pointer" }}
                      >
                        <td>
                          <div className="table-user-cell">
                            <span className="user-initials">
                              {employeeName.split(/\s+/).filter(Boolean).map((part) => part[0]).join("").slice(0, 3) || "E"}
                            </span>
                            <div>
                              <span className="user-name">{employeeName}</span>
                              <span className="user-sub-id">Réf: REQ-{request.id}</span>
                            </div>
                          </div>
                        </td>
                        <td>{request.leave_type_name || request.leave_type || "Congé"}</td>
                        <td>
                          <div style={{ display: "flex", flexDirection: "column" }}>
                            <span>Du {formatDate(request.start_date)}</span>
                            <span className="text-muted" style={{ fontSize: "0.8rem" }}>
                              Au {formatDate(request.end_date)}
                            </span>
                          </div>
                        </td>
                        <td className="text-center font-semibold text-primary">{request.duration_days ?? 0} jrs</td>
                        <td>
                          <span
                            className="text-muted"
                            style={{ fontSize: "0.9rem", fontStyle: request.reason ? "normal" : "italic" }}
                          >
                            {request.reason || "Aucun motif spécifié"}
                          </span>
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openDetails(request);
                            }}
                            className="btn btn-outline"
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", whiteSpace: "nowrap" }}
                            aria-label={`Voir les détails de la demande ${request.id}`}
                          >
                            <Eye size={14} /> Détails
                          </button>
                        </td>
                        <td>
                          <input
                            type="text"
                            onClick={(event) => event.stopPropagation()}
                            value={comments[request.id] || ""}
                            onChange={(event) => handleCommentChange(request.id, event.target.value)}
                            placeholder="Note de validation..."
                            className="form-input"
                            style={{ padding: "6px 10px", fontSize: "0.85rem" }}
                          />
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "8px", justifyContent: "center" }}>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDecision(request.id, true);
                              }}
                              className="btn btn-icon-group"
                              style={{ background: "#10b981", color: "white", padding: "6px 12px", border: "none", fontSize: "0.85rem" }}
                            >
                              <ThumbsUp size={14} /> <span>Valider</span>
                            </button>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDecision(request.id, false);
                              }}
                              className="btn btn-icon-group"
                              style={{ background: "#ef4444", color: "white", padding: "6px 12px", border: "none", fontSize: "0.85rem" }}
                            >
                              <ThumbsDown size={14} /> <span>Refuser</span>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredPendingRequests.length === 0 && (
                    <tr>
                      <td colSpan="8" className="text-center text-muted" style={{ padding: "30px" }}>
                        {requests.length === 0
                          ? "Aucune demande en attente de votre validation."
                          : "Aucune demande ne correspond aux filtres sélectionnés."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* History */}
          <section className="dashboard-section card-inner-box" style={{ marginTop: "30px" }}>
            <div className="card-box-header" style={{ marginBottom: "20px" }}>
              <h3>Historique de vos validations</h3>
              <p>Liste des demandes d'équipe déjà traitées.</p>
            </div>

            <FilterToolbar
              title="de l'historique"
              filters={historyFilters}
              setFilters={setHistoryFilters}
              statusOptions={historyStatusOptions}
              resultCount={filteredHistoryRequests.length}
              totalCount={historyRequests.length}
            />

            <div className="table-responsive-wrapper">
              <table className="custom-table">
                <thead>
                  <tr>
                    <th>Collaborateur</th>
                    <th>Type</th>
                    <th>Période</th>
                    <th className="text-center">Durée</th>
                    <th>Statut actuel</th>
                    <th>Votre commentaire</th>
                    <th>Date de décision</th>
                    <th className="text-center">Détails</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredHistoryRequests.map((request) => {
                    const employeeName = getEmployeeName(request);
                    const statusInfo = getStatusLabel(request.status);
                    return (
                      <tr
                        key={request.id}
                        onClick={() => openDetails(request)}
                        title="Cliquer pour voir les détails de la demande"
                        style={{ cursor: "pointer" }}
                      >
                        <td>
                          <div className="table-user-cell">
                            <span className="user-initials">
                              {employeeName.split(/\s+/).filter(Boolean).map((part) => part[0]).join("").slice(0, 3) || "E"}
                            </span>
                            <div>
                              <span className="user-name">{employeeName}</span>
                              <span className="user-sub-id">Réf: REQ-{request.id}</span>
                            </div>
                          </div>
                        </td>
                        <td>{request.leave_type_name || request.leave_type || "Congé"}</td>
                        <td>
                          <span>Du {formatDate(request.start_date)} au {formatDate(request.end_date)}</span>
                        </td>
                        <td className="text-center font-semibold text-primary">{request.duration_days ?? 0} jrs</td>
                        <td>
                          <span className={`badge ${statusInfo.className}`}>{statusInfo.label}</span>
                        </td>
                        <td>
                          <span className="text-muted" style={{ fontSize: "0.9rem" }}>
                            {request.manager_comment || "Aucun commentaire"}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted" style={{ fontSize: "0.85rem" }}>
                            {formatDate(request.manager_decision_date)}
                          </span>
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            onClick={(event) => {
                              event.stopPropagation();
                              openDetails(request);
                            }}
                            className="btn btn-outline"
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 10px", whiteSpace: "nowrap" }}
                            aria-label={`Voir les détails de la demande ${request.id}`}
                          >
                            <Eye size={14} /> Détails
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredHistoryRequests.length === 0 && (
                    <tr>
                      <td colSpan="8" className="text-center text-muted" style={{ padding: "20px" }}>
                        {historyRequests.length === 0
                          ? "Aucun historique de validation disponible."
                          : "Aucune demande ne correspond aux filtres sélectionnés."}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Fenêtre de détails rendue dans document.body pour rester visible */}
          {selectedRequest && createPortal((
            <div
              role="presentation"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) closeDetails();
              }}
              style={{
                position: "fixed",
                inset: 0,
                zIndex: 99999,
                background: "rgba(15, 23, 42, 0.48)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "20px",
                overflowY: "auto",
              }}
            >
              <section
                role="dialog"
                aria-modal="true"
                aria-labelledby="leave-details-title"
                onMouseDown={(event) => event.stopPropagation()}
                style={{
                  width: "min(900px, 100%)",
                  maxHeight: "90vh",
                  overflowY: "auto",
                  background: "#fff",
                  borderRadius: "16px",
                  boxShadow: "0 24px 70px rgba(0,0,0,.25)",
                }}
              >
                <header style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  gap: 16, padding: "18px 22px", borderBottom: "1px solid #e2e8f0",
                  position: "sticky", top: 0, background: "#fff", zIndex: 1,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ width: 42, height: 42, display: "grid", placeItems: "center",
                      background: "#eef2ff", color: "#4f46e5", borderRadius: 12 }}>
                      <ClipboardList size={21} />
                    </span>
                    <div>
                      <h2 id="leave-details-title" style={{ margin: 0, fontSize: "1.05rem", color: "#0f172a" }}>
                        Détails de la demande de congé
                      </h2>
                      <span style={{ color: "#64748b", fontSize: ".8rem" }}>
                        Référence : #{selectedRequest.id}
                      </span>
                    </div>
                  </div>
                  <button type="button" onClick={closeDetails} aria-label="Fermer les détails"
                    style={{ border: 0, background: "transparent", cursor: "pointer", color: "#64748b", padding: 6 }}>
                    <X size={22} />
                  </button>
                </header>

                <div style={{ padding: "22px", color: "#1e293b" }}>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 16 }}>
                    <div style={{ padding: 18, border: "1px solid #dbe4f0", borderRadius: 12, background: "#f8fafc" }}>
                      <h3 style={{ margin: "0 0 14px", fontSize: ".92rem", display: "flex", alignItems: "center", gap: 8 }}>
                        <UserRound size={16} /> Information employé
                      </h3>
                      <p style={{ margin: "7px 0" }}><strong>Nom :</strong> {getEmployeeName(selectedRequest)}</p>
                      <p style={{ margin: "7px 0" }}><strong>Type :</strong> {selectedRequest.leave_type_name || selectedRequest.leave_type || "Congé"}</p>
                      <p style={{ margin: "7px 0" }}><strong>Référence :</strong> REQ-{selectedRequest.id}</p>
                    </div>

                    <div style={{ padding: 18, border: "1px solid #dbe4f0", borderRadius: 12, background: "#f8fafc" }}>
                      <h3 style={{ margin: "0 0 14px", fontSize: ".92rem", display: "flex", alignItems: "center", gap: 8 }}>
                        <CalendarDays size={16} /> Détails de validation
                      </h3>
                      <p style={{ margin: "7px 0" }}><strong>Dates :</strong> du {formatDate(selectedRequest.start_date)} au {formatDate(selectedRequest.end_date)}</p>
                      <p style={{ margin: "7px 0" }}><strong>Durée :</strong> {selectedRequest.duration_days ?? "-"} jour(s)</p>
                      <p style={{ margin: "7px 0" }}>
                        <strong>Statut :</strong>{" "}
                        <span className={`badge ${getStatusLabel(selectedRequest.status).className}`}>
                          {getStatusLabel(selectedRequest.status).label}
                        </span>
                      </p>
                    </div>
                  </div>

                  <div style={{ marginTop: 20 }}>
                    <h3 style={{ fontSize: ".95rem", marginBottom: 8 }}>Motif de la demande</h3>
                    <div style={{ padding: 14, border: "1px solid #e2e8f0", borderRadius: 10, background: "#fff", whiteSpace: "pre-wrap" }}>
                      {selectedRequest.reason || "Aucun motif spécifié."}
                    </div>
                  </div>

                  <div style={{ marginTop: 20 }}>
                    <h3 style={{ fontSize: ".95rem", marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
                      <Paperclip size={16} /> Pièces jointes
                    </h3>
                    {attachmentsLoading ? (
                      <p style={{ color: "#64748b" }}>Chargement des pièces jointes…</p>
                    ) : attachmentsError ? (
                      <p role="alert" style={{ color: "#b91c1c" }}>{attachmentsError}</p>
                    ) : attachments.length > 0 ? (
                      <ul style={{ margin: 0, padding: "12px 12px 12px 32px", border: "1px solid #e2e8f0", borderRadius: 10 }}>
                        {attachments.map((file, index) => {
                          const name = file.original_name || file.original_filename || file.filename || file.file_name || file.name || `Pièce jointe ${index + 1}`;
                          const href = file.download_url || file.file_url || file.url || file.href;
                          return (
                            <li key={file.id || file.attachment_id || `${name}-${index}`} style={{ margin: "6px 0" }}>
                              {href ? (
                                <a href={href} target="_blank" rel="noreferrer" style={{ color: "#4f46e5" }}>{name}</a>
                              ) : (
                                <span>{name}</span>
                              )}
                              {file.description && <span style={{ color: "#64748b" }}> — {file.description}</span>}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <div style={{ padding: 14, border: "1px dashed #cbd5e1", borderRadius: 10, color: "#64748b", textAlign: "center" }}>
                        Aucune pièce jointe pour cette demande.
                      </div>
                    )}
                  </div>

                  {(selectedRequest.manager_comment || selectedRequest.hr_comment || selectedRequest.manager_decision_date) && (
                    <div style={{ marginTop: 20 }}>
                      <h3 style={{ fontSize: ".95rem", marginBottom: 8 }}>Commentaires et suivi</h3>
                      {selectedRequest.manager_comment && <p><strong>Commentaire Manager :</strong> {selectedRequest.manager_comment}</p>}
                      {selectedRequest.hr_comment && <p><strong>Commentaire RH :</strong> {selectedRequest.hr_comment}</p>}
                      {selectedRequest.manager_decision_date && <p><strong>Date de décision Manager :</strong> {formatDate(selectedRequest.manager_decision_date)}</p>}
                    </div>
                  )}

                  {normalizeStatus(selectedRequest.status) === "PENDING_MANAGER" && (
                    <div style={{ marginTop: 22, paddingTop: 18, borderTop: "1px solid #e2e8f0" }}>
                      <label htmlFor="manager-detail-comment" style={{ display: "block", fontWeight: 700, fontSize: ".85rem", marginBottom: 7 }}>
                        Commentaire de validation (facultatif)
                      </label>
                      <textarea
                        id="manager-detail-comment"
                        value={comments[selectedRequest.id] || ""}
                        onChange={(event) => handleCommentChange(selectedRequest.id, event.target.value)}
                        placeholder="Ajouter une note avant de valider ou refuser…"
                        rows={3}
                        className="form-input"
                        style={{ width: "100%", boxSizing: "border-box", resize: "vertical" }}
                      />
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                        <button type="button" onClick={async () => { await handleDecision(selectedRequest.id, false); closeDetails(); }}
                          className="btn" style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#ef4444", color: "#fff", border: 0 }}>
                          <ThumbsDown size={15} /> Refuser
                        </button>
                        <button type="button" onClick={async () => { await handleDecision(selectedRequest.id, true); closeDetails(); }}
                          className="btn" style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "#10b981", color: "#fff", border: 0 }}>
                          <ThumbsUp size={15} /> Valider et transmettre aux RH
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>
          ), document.body)}
        </main>
      </div>
    </div>
  );
};

export default TeamLeaveRequests;
